'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Icon } from './icons';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/lib/toast-context';
import { clientActiveRentals, clientCheckout } from '@/lib/client-api';
import type { Book } from '@/types/catalog';

interface RentalGateButtonProps {
  book: Book;
  big?: boolean;
}

// Real checkout against fce-books-api. The API is the source of truth for the
// loan gates (2 active / 4 lifetime); this component just maps its errors.
export function RentalGateButton({ book, big = true }: RentalGateButtonProps) {
  const { loggedIn, hydrating } = useAuth();
  const router = useRouter();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [hasLoan, setHasLoan] = useState(false);
  const [blockMsg, setBlockMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!loggedIn) { setHasLoan(false); return; }
    let cancelled = false;
    clientActiveRentals().then((items) => {
      if (!cancelled) setHasLoan(items.some((r) => r.bookId === book.id));
    });
    return () => { cancelled = true; };
  }, [loggedIn, book.id]);

  const sizing = big ? { padding: '16px 26px', fontSize: 15 } : {};
  const isAudio = book.product === 'audiobook';

  if (hasLoan) {
    return (
      <Link href={`${isAudio ? '/escuchar' : '/lectura'}?bookId=${book.id}`} className="btn btn-primary" style={sizing}>
        <Icon.book />
        <span>{isAudio ? 'Continuar escuchando' : 'Continuar leyendo'}</span>
      </Link>
    );
  }

  const handleClick = async () => {
    if (hydrating || busy) return;
    if (!loggedIn) {
      router.push('/iniciar-sesion');
      return;
    }
    setBusy(true);
    const res = await clientCheckout(book.id);
    setBusy(false);
    if (res.ok) {
      setHasLoan(true);
      toast.show('Has tomado prestado este título por 30 días.', 'success');
      return;
    }
    if (res.error === 'ACTIVE_RENTAL_EXISTS') {
      setBlockMsg('Has alcanzado el límite de 2 préstamos activos. Devuelve uno para tomar otro.');
    } else if (res.error === 'LIFETIME_CAP_REACHED') {
      setBlockMsg('Has llegado al máximo de 4 préstamos de este título.');
    } else {
      toast.show('No pudimos completar el préstamo. Inténtalo de nuevo.', 'error');
    }
  };

  const btn = (
    <button onClick={handleClick} className="btn btn-primary" disabled={busy || !!blockMsg} style={sizing}>
      <Icon.book />
      <span>{!loggedIn && !hydrating ? 'Iniciar sesión para leer' : busy ? 'Un momento…' : 'Tomar prestado por 30 días'}</span>
    </button>
  );

  if (!blockMsg) return btn;
  return (
    <span className="tt-wrap">
      {btn}
      <span className="tt" role="tooltip">
        <Icon.info style={{ verticalAlign: '-2px', marginRight: 6 }} />
        {blockMsg}
      </span>
    </span>
  );
}
